// src/components/SimpleComponents/SimpleSelectAsync.jsx
// *******************************************************************************
// GENERIC ASYNC SELECT
// *******************************************************************************
// Generic async select component for server-side search.
// Accepts an `onFetch` callback that receives the search value and must return
// an array of options: [{ value, label }].
// Accepts an optional `onFetchByValue` callback to resolve the label for an
// existing form value on mount (e.g., after page refresh).
// Handles debouncing, loading states, viewMode integration, and validation.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Select, Spin, Flex, Typography, Empty, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelectAsync = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder = 'type_to_search',
  onChange,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  allowClear = true,

  // Async-specific props
  onFetch, // (searchValue: string) => Promise<[{ value, label }]>
  onFetchByValue, // (value: string) => Promise<{ value, label } | null>
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  emptyText = 'no_data',
  searchHintText = 'type_to_search',
  suffixIcon: suffixIconProp,
  optionRender: optionRenderProp,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const debounceTimerRef = useRef(null);
  const lastSearchRef = useRef('');
  const initialValueLoadedRef = useRef(false);

  // Watch field value for validation and initial load
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-load all options on mount when minSearchLength is 0
  useEffect(() => {
    if (minSearchLength !== 0 || !onFetch) return;

    setLoading(true);
    onFetch('')
      .then(results => {
        setOptions(Array.isArray(results) ? results : []);
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load initial option(s) when form has a pre-existing value (e.g., after F5)
  useEffect(() => {
    if (initialValueLoadedRef.current) return;
    if (!onFetchByValue) return;

    // Handle multiple mode: fieldValue is an array
    if (Array.isArray(fieldValue)) {
      if (fieldValue.length === 0) return;

      const missingIds = fieldValue.filter(
        id => id && !options.some(opt => opt.value === id)
      );
      if (missingIds.length === 0) return;

      initialValueLoadedRef.current = true;

      Promise.all(missingIds.map(id => onFetchByValue(id).catch(() => null)))
        .then(results => {
          const valid = results.filter(Boolean);
          if (valid.length > 0) {
            setOptions(prev => {
              const existingValues = new Set(prev.map(o => o.value));
              const newOpts = valid.filter(o => !existingValues.has(o.value));
              return [...newOpts, ...prev];
            });
          }
        })
        .catch(err => {
          console.error('[SimpleSelectAsync] Error loading initial values:', err);
        });

      return;
    }

    // Single mode: fieldValue is a scalar
    if (!fieldValue) return;

    const hasOption = options.some(opt => opt.value === fieldValue);
    if (hasOption) return;

    initialValueLoadedRef.current = true;

    // Fetch the label for the existing value
    onFetchByValue(fieldValue)
      .then(option => {
        if (option) {
          setOptions(prev => {
            const exists = prev.some(opt => opt.value === option.value);
            return exists ? prev : [option, ...prev];
          });
        }
      })
      .catch(err => {
        console.error('[SimpleSelectAsync] Error loading initial value:', err);
      });
  }, [fieldValue, onFetchByValue, options]);

  // Activate validation when form shows errors
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Validate field value
  useEffect(() => {
    if (!shouldValidate) return;

    const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

    if (!hasValue && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (hasValue) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    searchValue => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Skip if search is too short
      if (!searchValue || searchValue.length < minSearchLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      // Skip if same search
      const normalizedSearch = searchValue.toLowerCase().trim();
      if (normalizedSearch === lastSearchRef.current) {
        return;
      }

      setLoading(true);

      // Debounce the actual search
      debounceTimerRef.current = setTimeout(async () => {
        try {
          lastSearchRef.current = normalizedSearch;

          const results = await onFetch(searchValue);
          setOptions(Array.isArray(results) ? results : []);
        } catch (error) {
          console.error('[SimpleSelectAsync] Search error:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceDelay);
    },
    [minSearchLength, debounceDelay, onFetch]
  );

  const handleSelect = useCallback(
    (value, option) => {
      setOpen(false);
      setShouldValidate(true);

      if (typeof onChange === 'function') {
        onChange(value, option);
      }
    },
    [onChange]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
    if (!visible) {
      lastSearchRef.current = '';
    }
  }, []);

  const handleBlur = useCallback(
    ev => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (typeof onBlur === 'function') {
        setTimeout(() => onBlur(ev), 0);
      }
    },
    [onBlur]
  );

  const handleClear = useCallback(() => {
    setOptions([]);
    lastSearchRef.current = '';
    initialValueLoadedRef.current = false;
    setShouldValidate(true);

    if (typeof onChange === 'function') {
      onChange(null, null);
    }
  }, [onChange]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Spin size="small" />
        </Flex>
      );
    }

    if (lastSearchRef.current && options.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX(emptyText)}
          style={{ margin: '16px 0' }}
        />
      );
    }

    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Text type="secondary">{translateX(searchHintText)}</Text>
      </Flex>
    );
  }, [loading, options.length, emptyText, searchHintText]);

  const defaultSuffixIcon = loading ? <Spin size="small" /> : <SearchOutlined />;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        style={{ height: 40, ...style }}
        showSearch
        allowClear={allowClear}
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        onBlur={handleBlur}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        notFoundContent={notFoundContent}
        loading={loading}
        suffixIcon={suffixIconProp !== undefined ? suffixIconProp : defaultSuffixIcon}
        options={options}
        optionRender={optionRenderProp}
        labelInValue={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSelectAsync;
